import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar, CalendarDayButton } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDayCounts } from "@/hooks/use-day-counts";

function isoLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parses a YYYY-MM-DD string as a *local* date (avoids the UTC-midnight
 *  off-by-one `new Date("2026-07-17")` gives in negative-UTC-offset zones). */
function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export interface DateCountPickerProps {
  date: string;
  onChange: (date: string) => void;
  fetchCounts: (from: string, to: string) => Promise<Record<string, number>>;
  /** Changes whenever cached counts should be dropped (feature/site switch). */
  cacheKey: string;
  /** "route" | "order" — used in the day badge's title tooltip. */
  noun: string;
}

/** Date picker with per-day count badges, replacing flatpickr — ports the old
 *  main.ts calendar (onDayCreate badge stamping) onto shadcn's Calendar via
 *  its `components.DayButton` override point. */
export function DateCountPicker({ date, onChange, fetchCounts, cacheKey, noun }: DateCountPickerProps) {
  const [open, setOpen] = useState(false);
  const { counts, ensureMonth } = useDayCounts(fetchCounts, cacheKey);
  const selected = parseLocalDate(date);
  const [month, setMonth] = useState(selected);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) ensureMonth(month.getFullYear(), month.getMonth());
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className="justify-start font-normal">
          <CalendarIcon />
          {date}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          month={month}
          onMonthChange={(m) => {
            setMonth(m);
            ensureMonth(m.getFullYear(), m.getMonth());
          }}
          onSelect={(d) => {
            if (!d) return;
            onChange(isoLocal(d));
            setOpen(false);
          }}
          components={{
            DayButton: (props) => {
              const iso = isoLocal(props.day.date);
              const n = counts[iso];
              return (
                <div className="relative">
                  <CalendarDayButton {...props} />
                  {!!n && (
                    <span
                      className="pointer-events-none absolute right-0.5 bottom-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-0.5 text-[9px] leading-none font-medium text-primary-foreground"
                      title={`${n} ${noun}${n === 1 ? "" : "s"}`}
                    >
                      {n > 99 ? "99+" : n}
                    </span>
                  )}
                </div>
              );
            },
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
